/* eslint-disable @typescript-eslint/consistent-type-definitions -- Zero mutator/query arg types must be `type` aliases to satisfy the ReadonlyJSONValue constraint; interfaces lack an implicit index signature. */
import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"

import type { MutatorTx, Schema } from "../schema"
import type { DeleteID } from "@rocicorp/zero"

export type CreateBlockedSenderArgs = {
	id: string
	value: string
	type: "EMAIL" | "DOMAIN"
}

function normalizeEmail(email: string): string {
	return email.toLowerCase().trim()
}

export async function findThreadSender(
	tx: MutatorTx,
	workspaceId: string,
	threadId: string,
): Promise<{ email: string; isBlocked: boolean }> {
	const thread = await tx.query.feedbacks
		.where("id", "=", threadId)
		.where("workspaceId", "=", workspaceId)
		.one()
		.run()
	if (!thread) throw new Error("Thread not found")

	const contact = thread.contactId
		? await tx.query.contacts
				.where("id", "=", thread.contactId)
				.where("workspaceId", "=", workspaceId)
				.one()
				.run()
		: undefined
	const email = normalizeEmail(contact?.email ?? "")
	if (!email.includes("@")) throw new Error("This thread has no sender email")

	const domain = email.split("@")[1] ?? ""
	const blocks = await tx.query.blockedSender
		.where("workspaceId", "=", workspaceId)
		.where("value", "IN", domain ? [email, domain] : [email])
		.run()
	const isBlocked = blocks.some(
		(block) =>
			(block.type === "EMAIL" && block.value === email) ||
			(block.type === "DOMAIN" && block.value === domain),
	)

	return { email, isBlocked }
}

export const blockedSenderMutators = {
	create: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: CreateBlockedSenderArgs
			ctx: ZeroJwtPayload
		}) => {
			const normalizedValue = normalizeEmail(args.value)

			const [existing] = await tx.query.blockedSender
				.where("workspaceId", "=", authData.workspaceId)
				.where("value", "=", normalizedValue)
				.run()

			if (existing) {
				throw new Error("Blocked sender already exists")
			}

			await tx.mutate.blockedSender.insert({
				id: args.id,
				createdAt: Date.now(),
				workspaceId: authData.workspaceId,
				value: normalizedValue,
				type: args.type,
			})
		},
	),
	delete: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: DeleteID<Schema["tables"]["blockedSender"]>
		}) => {
			await tx.mutate.blockedSender.delete({ id: args.id })
		},
	),
}
